import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { TabComponent, TabItemDirective, TabItemsDirective } from '@syncfusion/ej2-react-navigations';

export interface ReactTabItem {
  header: string;
  content: () => ReactNode;
}

export interface ReactTabsProps {
  items: ReactTabItem[];
  /** Controlled selection. Leave it out to let the component keep the index itself. */
  selectedIndex?: number;
  onSelect?: (index: number) => void;
  className?: string;
  style?: CSSProperties;
  panelClassName?: string;
  panelStyle?: CSSProperties;
}

/**
 * Syncfusion tab bar whose active panel is rendered by React.
 *
 * Do not pass inline functions to Syncfusion's `TabItemDirective content`: a new function on
 * every render makes Syncfusion rebuild the whole panel. All DOM inside is replaced, so
 * scroll positions and component state are lost whenever the parent state changes, for
 * example on every click in a long checkbox list. Here Syncfusion only draws the headers and
 * the panel stays in the normal React tree. Only the active panel is mounted, like
 * `loadOn="Dynamic"`.
 */
export function ReactTabs({
  items,
  selectedIndex,
  onSelect,
  className,
  style,
  panelClassName,
  panelStyle,
}: ReactTabsProps) {
  const [internalIndex, setInternalIndex] = useState(0);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const current = Math.min(selectedIndex ?? internalIndex, Math.max(items.length - 1, 0));

  return (
    <div
      className={`pb-react-tabs ${className ?? ''}`}
      style={{ display: 'flex', flexDirection: 'column', minHeight: 0, ...style }}
    >
      <TabComponent
        cssClass="pb-react-tabs__header"
        selectedItem={current}
        selected={(e: { selectedIndex?: number }) => {
          // Syncfusion may keep the handler from the first render, so read nothing from the
          // render scope here except stable setters and refs.
          if (e.selectedIndex === undefined) return;
          setInternalIndex(e.selectedIndex);
          onSelectRef.current?.(e.selectedIndex);
        }}
      >
        <TabItemsDirective>
          {items.map((item, index) => (
            <TabItemDirective key={index} header={{ text: item.header }} />
          ))}
        </TabItemsDirective>
      </TabComponent>
      <div
        className={`pb-react-tabs__panel ${panelClassName ?? ''}`}
        style={{ flex: '1 1 0%', minHeight: 0, display: 'flex', flexDirection: 'column', ...panelStyle }}
      >
        {items[current]?.content()}
      </div>
    </div>
  );
}
